import { useFormContext } from 'react-hook-form';
import { cn } from '@/lib/utils/cn';
import type { TemplateId, ThemeId } from '@/types/portfolio';
import { TEMPLATE_CATALOG, THEME_CATALOG } from '@/features/templates/catalog';
import { TemplateThumb } from '@/features/templates/components/TemplateThumb';
import type { OnboardingFormValues } from '../schema';

export function TemplateStep() {
  const form = useFormContext<OnboardingFormValues>();
  const template = form.watch('template');
  const theme = form.watch('theme');

  function setTemplate(id: TemplateId) {
    form.setValue('template', id, { shouldValidate: true, shouldDirty: true });
  }
  function setTheme(id: ThemeId) {
    form.setValue('theme', id, { shouldValidate: true, shouldDirty: true });
  }

  return (
    <div className="space-y-10">
      <div>
        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          Template · {TEMPLATE_CATALOG.length} options
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          {TEMPLATE_CATALOG.map((t) => {
            const active = t.id === template;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTemplate(t.id)}
                className={cn(
                  'group relative overflow-hidden rounded-xl border text-left transition-all',
                  active
                    ? 'border-ink shadow-float'
                    : 'border-line bg-board shadow-card hover:border-ink/40',
                )}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-rail">
                  <TemplateThumb id={t.id} />
                  {active ? (
                    <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[11px] text-board">
                      ✓
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-line px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-ink">{t.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-faint">
                      {t.subtitle}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          Palette
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {THEME_CATALOG.map((t) => {
            const active = t.id === theme;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTheme(t.id)}
                className={cn(
                  'group flex items-center gap-3 rounded-lg border bg-board p-3 text-left transition-colors',
                  active ? 'border-ink' : 'border-line hover:border-ink/40',
                )}
              >
                <span className="flex h-10 w-10 shrink-0 overflow-hidden rounded-md border border-line">
                  <span className="flex-1" style={{ background: t.colors[0] }} />
                  <span className="flex-1" style={{ background: t.colors[1] }} />
                  <span className="flex-1" style={{ background: t.colors[2] }} />
                </span>
                <span>
                  <span className="block text-sm font-medium text-ink">{t.name}</span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.15em] text-ink-faint">
                    {t.mood}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
